import type { FinanceReadiness } from '../../executionOperations/types';
import type { ExecutionProgressHydration } from '../../executionOperations/types';
import { deriveRouteOperationalStatus } from './routeOperationalStatus';
import type { ExecutionIncident } from './incidentFacade';
import { hasActiveBlockingIncidents, getIncidentTypeLabel } from './incidentFacade';

export type FinanceReadinessView = {
  headline: string;
  detail: string;
  invoiceReady: boolean;
  updReady: boolean;
  originalsReady: boolean;
  allReady: boolean;
  blocked: boolean;
  blockReasons: string[];
  reasons: string[];
};

const REASON_LABELS: Record<string, string> = {
  loading_not_complete: 'Ожидается погрузка',
  unloading_not_complete: 'Ожидается выгрузка',
  cargo_not_delivered: 'Груз не доставлен',
  execution_not_complete: 'Исполнение не завершено',
  documents_pending: 'Ожидаются документы',
  partial_delivery: 'Частичная доставка',
  open_incidents: 'Есть открытые инциденты',
  downtime_unresolved: 'Не закрыт простой',
  expenses_unconfirmed: 'Не подтверждены расходы'
};

export function deriveFinanceReadinessView(params: {
  readiness: FinanceReadiness | null | undefined;
  hydration?: ExecutionProgressHydration | null;
  openDocumentTasks?: number;
  incidents?: ExecutionIncident[];
}): FinanceReadinessView {
  const { readiness, hydration, openDocumentTasks = 0, incidents = [] } = params;
  const blockingIncidents = hasActiveBlockingIncidents(incidents);
  const blockReasons: string[] = [];

  if (blockingIncidents) {
    for (const inc of incidents.filter((i) => i.status === 'open' && i.blockingFlag)) {
      blockReasons.push(getIncidentTypeLabel(inc.incidentType));
    }
  }
  if (hydration?.progress.blockers.includes('partial_delivery')) {
    blockReasons.push('Частичная доставка');
  }
  if (incidents.some((i) => i.status === 'open' && i.incidentType === 'downtime')) {
    blockReasons.push('Не закрыт простой');
  }
  if (openDocumentTasks > 0) {
    blockReasons.push('Ожидаются документы');
  }

  if (!readiness) {
    return {
      headline: 'Ожидается погрузка',
      detail: 'Данные о готовности к оплате появятся после начала исполнения',
      invoiceReady: false,
      updReady: false,
      originalsReady: false,
      allReady: false,
      blocked: blockingIncidents,
      blockReasons,
      reasons: []
    };
  }

  const routeStatus = hydration ? deriveRouteOperationalStatus(hydration, incidents) : null;
  const engineReady = readiness.invoiceReady && readiness.updReady && readiness.originalsReady;
  const blocked = blockingIncidents || blockReasons.length > 0;
  const allReady = engineReady && !blocked;

  let headline = 'Готов к оплате';
  if (blocked) {
    headline = 'Оплата заблокирована';
  } else if (!engineReady) {
    if (routeStatus?.key === 'created' || readiness.reasons.includes('loading_not_complete')) {
      headline = 'Ожидается погрузка';
    } else if (routeStatus?.key === 'in_transit') {
      headline = 'В пути';
    } else if (readiness.reasons.includes('unloading_not_complete') || routeStatus?.key === 'unloading') {
      headline = 'Ожидается выгрузка';
    } else if (openDocumentTasks > 0) {
      headline = 'Ожидаются документы';
    } else if (readiness.reasons.includes('cargo_not_delivered')) {
      headline = 'Ожидается выгрузка';
    } else {
      headline = 'Ожидаются документы';
    }
  }

  const reasons = readiness.reasons.map((r) => REASON_LABELS[r] ?? r);
  if (openDocumentTasks > 0 && !reasons.includes('Ожидаются документы')) {
    reasons.push('Ожидаются документы');
  }

  const detail = blocked
    ? blockReasons.join(' · ') || 'Есть блокирующие инциденты'
    : allReady
      ? 'Все проверки для выставления документов пройдены'
      : reasons.join(' · ') || headline;

  return {
    headline,
    detail,
    invoiceReady: readiness.invoiceReady,
    updReady: readiness.updReady,
    originalsReady: readiness.originalsReady,
    allReady,
    blocked,
    blockReasons,
    reasons
  };
}

export function financeReadinessTagLabel(ready: boolean): string {
  return ready ? 'Готово' : 'Не готово';
}
